import { FeatureFlagStore } from '../stores/featureFlag';
import { getCompaniesSelect } from './companies';
import { getUsersSelect } from './users';
import { IBodyItemResponse, IBodyListResponse, IRequestAPIBody, apiManager } from '../REST';
import {
    FeatureFlagsList,
    IFeatureFlagApi,
    IFeatureFlagApiAttributes,
    IFeatureFlags,
    IFeatureFlagsApi,
    IPage,
    IPostItem,
} from '../interfaces';
import { IMapField, mapApi2UiField, sortByString } from '../instruments';

const isAvailableFeatureFlags = (flag: string): boolean => {
    const forbiddenFlags = ['read_big_tables_from_replica', 'mfa', 'new_reports_page'];
    return !forbiddenFlags.includes(flag);
};

export const removeForbiddenFeatureFlags = (flags: IFeatureFlagsApi): IFeatureFlagsApi => {
    const result = { ...flags };
    Object.keys(flags).forEach((key: string) => {
        if (!isAvailableFeatureFlags(key)) {
            delete result[key as keyof IFeatureFlagsApi];
        }
    });

    return result;
};

export function formatFeatureFlagList(resp: IBodyListResponse): FeatureFlagsList {
    const { feature_flags } = resp as IFeatureFlagApi;

    return (
        feature_flags
            .filter((flag: IFeatureFlagApiAttributes) => isAvailableFeatureFlags(flag.key))
            .map((flag: IFeatureFlagApiAttributes) => {
                const parsedAttributes = mapApi2UiField(flag);
                const parsedConditions = mapApi2UiField(flag.conditions);

                return {
                    ...parsedAttributes,
                    conditions: {
                        companyIds: parsedConditions.companyIds?.join(','),
                        userIds: parsedConditions.userIds?.join(','),
                    },
                };
            }) as FeatureFlagsList
    ).sort((a, b) => sortByString(a.key, b.key));
}

export function setFeatureFlagList(list: FeatureFlagsList) {
    const FeatureFlagStoreInstance = FeatureFlagStore.getInstance();
    FeatureFlagStoreInstance.setFeatureFlagsList(list);
}

export async function getFeatureFlagsListAll(): Promise<FeatureFlagsList> {
    const { resp } = await apiManager.getApi('GET_FEATURE_FLAG_LIST_ALL');

    const flagList: FeatureFlagsList = formatFeatureFlagList(resp as IBodyListResponse);

    setFeatureFlagList(flagList);

    return flagList;
}

function formatFeatureFlagUpdateBody(fields: IMapField) {
    return {
        value: fields?.value,
        conditions: {
            company_ids: Array.from(new Set(fields.companyIds?.map((value: any) => value.trim()))) || [],
            user_ids: Array.from(new Set(fields.userIds?.map((value: any) => value.trim()))) || [],
        },
        description: fields?.description,
    };
}

export async function updateFeatureFlag(id: string, fields: IMapField): Promise<IPostItem> {
    const apiBody = formatFeatureFlagUpdateBody(fields) as IRequestAPIBody;

    const { resp, isError } = await apiManager.getApi('PATCH_FEATURE_FLAGS', { id: id }, apiBody);
    const response = resp as IBodyItemResponse;

    return { isError, errors: resp.errors, resp: response };
}

export async function getFeatureFlagsRelations(): Promise<void> {
    getUsersSelect();
    getCompaniesSelect();
}

export function isCorrectFlag(p: IPage, featureFlags: IFeatureFlags): boolean {
    if (!p.featureFlag) {
        return true;
    }

    return p.featureFlag.some((flag: string) => {
        return flag in featureFlags && featureFlags[flag as keyof IFeatureFlags];
    });
}
